import { GET } from '../dataSaver';
import { withQueryParams } from './portalTilesApi';
import { PARTNERS_URL } from './partnersApi';
import { FormatPhoneNumber } from '../../utils/formatting';

/** Normalize API list / single responses into an array of prospects. */
export const normalizeProspectsList = (data) => {
  if (Array.isArray(data)) return data.filter(Boolean);
  if (Array.isArray(data?.prospects)) return data.prospects.filter(Boolean);
  if (Array.isArray(data?.content)) return data.content.filter(Boolean);
  if (Array.isArray(data?.data)) return data.data.filter(Boolean);
  if (Array.isArray(data?.results)) return data.results.filter(Boolean);
  if (data && typeof data === 'object' && (data.prospectId || data.id)) {
    return [data];
  }
  return [];
};

/**
 * Normalize prospect from HubSpot / CRM search APIs.
 * Stores the fields the UI needs; create payloads send only prospectId.
 */
export const normalizeProspect = (prospect) => {
  if (prospect == null || prospect === '') return null;
  if (typeof prospect !== 'object') return null;
  const prospectId = String(
    prospect.prospectId || prospect.id || prospect.hsObjectId || ''
  ).trim();
  if (!prospectId) return null;
  const firstName = String(prospect.firstName || '').trim();
  const lastName = String(prospect.lastName || '').trim();
  const name =
    String(prospect.name || '').trim() ||
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    String(prospect.email || '').trim() ||
    prospectId;
  return {
    prospectId,
    firstName,
    lastName,
    name,
    email: String(prospect.email || '').trim(),
    organizationName: prospect.organizationName || null,
    title: String(prospect.title || '').trim(),
    phone: String(prospect.phone || '').trim(),
    crmProvider: String(prospect.crmProvider || '').trim(),
    partnerId: String(prospect.partnerId || '').trim(),
    partnerName: String(prospect.partnerName || '').trim(),
  };
};

export const getProspectLabel = (prospect) => {
  const normalized = normalizeProspect(prospect);
  if (!normalized) return '';
  if (normalized.email) {
    return `${normalized.name} (${normalized.email})`;
  }
  return normalized.name;
};

/** Demo Studio create/update — backend re-fetches canonical CRM data. */
export const toDemoShareProspectPayload = (prospect) => {
  const normalized = normalizeProspect(prospect);
  if (!normalized?.prospectId) return null;
  return { prospectId: normalized.prospectId };
};

/**
 * Map CRM prospect/contact fields onto a Demo Studio recipient form row.
 * Splits `name` when first/last are missing. Includes optional mobileNumber.
 */
export const recipientFieldsFromProspect = (prospect) => {
  const normalized = normalizeProspect(prospect);
  if (!normalized) return null;
  let firstName = normalized.firstName;
  let lastName = normalized.lastName;
  if (!firstName && !lastName && normalized.name) {
    const parts = normalized.name.split(/\s+/).filter(Boolean);
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ') || '';
  }
  return {
    firstName,
    lastName,
    email: normalized.email || '',
    mobileNumber: FormatPhoneNumber(normalized.phone || '') || '',
    title: normalized.title || '',
  };
};

export const fetchPartnerProspects = async (
  partnerId,
  { search, limit = 20 } = {}
) => {
  const id = String(partnerId || '').trim();
  if (!id) return [];
  const { data } = await GET(
    withQueryParams(`${PARTNERS_URL}/${encodeURIComponent(id)}/prospects`, {
      search: String(search || '').trim() || undefined,
      limit,
    })
  );
  return normalizeProspectsList(data).map(normalizeProspect).filter(Boolean);
};

export const fetchPartnerProspectById = async (partnerId, prospectId) => {
  const partner = String(partnerId || '').trim();
  const prospect = String(prospectId || '').trim();
  if (!partner || !prospect) return null;
  const { data } = await GET(
    `${PARTNERS_URL}/${encodeURIComponent(partner)}/prospects/${encodeURIComponent(prospect)}`
  );
  return normalizeProspect(data);
};
